/**
 * Validation utilities for Query Builder
 * Проверка валидности узлов дерева фильтров
 */

import { ZOperatorsMapping } from "../types/ZFilterMetadataTypes";
import {
  isGroupNode,
  ZCriterionNode,
  ZGroupNode,
  ZQueryBuilderNode,
} from "../types/ZQueryBuilderTypes";
import {
  validationContexts,
  ZValidationContext,
} from "../types/ZValidationContext";
import { operatorRequiresValue } from "./filterUtils";

/**
 * Результат валидации узла
 */
export type ValidationResult = {
  /** Валиден ли узел */
  isValid: boolean;
  /** Список ошибок */
  errors: string[];
};

/**
 * Результат валидации дерева
 */
export type TreeValidationResult = {
  /** Валидно ли дерево */
  isValid: boolean;
  /** Общий список ошибок */
  errors: string[];
  /** Ошибки по id узла */
  nodeErrorsById: Record<string, string[]>;
  /** Количество невалидных критериев */
  invalidCriteriaCount: number;
  /** Общее количество критериев */
  criteriaCount: number;
};

/**
 * Проверка валидности критерия
 * @param node - узел-критерий
 * @param operatorsMapping - маппинг операторов (опционально)
 * @returns результат валидации
 */
export const validateCriterion = (
  node: ZCriterionNode,
  operatorsMapping: ZOperatorsMapping,
  context: ZValidationContext = validationContexts.apply,
): ValidationResult => {
  const errors: string[] = [];

  // Проверка наличия метаданных поля
  if (!node.fieldMeta) {
    errors.push("Поле не может быть пустым");
  }

  // Проверка наличия типа значения
  if (node.fieldMeta && !node.valueType) {
    errors.push("Поле не может быть пустым");
  }

  // Проверка наличия оператора
  if (!node.operator) {
    errors.push("Поле не может быть пустым");
  }

  // Проверка значения (если оператор требует значение)
  if (node.operator && node.valueType) {
    const operators = operatorsMapping[node.valueType];
    if (
      context !== validationContexts.saveTemplate &&
      operatorRequiresValue(node.operator, operators ?? [])
    ) {
      if (
        node.value === null ||
        node.value === undefined ||
        node.value === ""
      ) {
        errors.push("Поле не может быть пустым");
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Проверка валидности группы
 * @param node - узел-группа
 * @returns результат валидации
 */
export const validateGroup = (node: ZGroupNode): ValidationResult => {
  const errors: string[] = [];

  if (!node.op) {
    errors.push("Логический оператор не задан");
  }

  if (node.items.length === 0) {
    errors.push("Группа не содержит условий");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Рекурсивная проверка валидности дерева
 * @param node - корневой узел
 * @param operatorsMapping - маппинг операторов
 * @returns результат валидации дерева
 */
export const validateTree = (
  node: ZGroupNode,
  operatorsMapping: ZOperatorsMapping,
  context: ZValidationContext = validationContexts.apply,
): TreeValidationResult => {
  const errors: string[] = [];
  const nodeErrorsById: Record<string, string[]> = {};
  let invalidCriteriaCount = 0;
  let criteriaCount = 0;

  const pushNodeErrors = (nodeId: string, nodeErrors: string[]): void => {
    if (nodeErrors.length === 0) {
      return;
    }

    if (nodeErrorsById[nodeId]) {
      nodeErrorsById[nodeId].push(...nodeErrors);
      return;
    }

    nodeErrorsById[nodeId] = [...nodeErrors];
  };

  const traverse = (
    currentNode: ZQueryBuilderNode,
    path: number[] = [],
  ): void => {
    const pathStr = path.length > 0 ? `[${path.join(", ")}]` : "root";

    if (isGroupNode(currentNode)) {
      // Валидация группы
      const groupResult = validateGroup(currentNode);
      if (!groupResult.isValid) {
        pushNodeErrors(currentNode.id, groupResult.errors);
        groupResult.errors.forEach((error) => {
          errors.push(`Group ${pathStr}: ${error}`);
        });
      }

      // Рекурсивная валидация дочерних элементов
      currentNode.items.forEach((child, index) => {
        traverse(child, [...path, index]);
      });
    } else {
      // Валидация критерия
      criteriaCount++;
      const criterionResult = validateCriterion(
        currentNode,
        operatorsMapping,
        context,
      );
      if (!criterionResult.isValid) {
        invalidCriteriaCount++;
        pushNodeErrors(currentNode.id, criterionResult.errors);
        criterionResult.errors.forEach((error) => {
          errors.push(`Criterion ${pathStr}: ${error}`);
        });
      }
    }
  };

  traverse(node);

  if (criteriaCount === 0) {
    const rootError = "Должен быть задан хотя бы один критерий";
    errors.push(`Group root: ${rootError}`);
    pushNodeErrors(node.id, [rootError]);
  }

  return {
    isValid: errors.length === 0,
    errors,
    nodeErrorsById,
    invalidCriteriaCount,
    criteriaCount,
  };
};
